import {
  Building2,
  CircleHelp,
  ChevronLeft,
  DoorOpen,
  Grid2X2,
  HandHeart,
  Home,
  Info,
  Lamp,
  LogIn,
  LogOut,
  Menu,
  MessageCircle,
  PackagePlus,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  TreePine,
  UploadCloud,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  clearLocalStoragePreserveMls,
  clearSessionStoragePreserveMls,
} from '../../e2ee/mlsStorageUtils.js';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPrimaryNav } from '../../config/appNav';
import NightModeToggle from '../../containers/NewDeceased/atoms/NightModeToggle';
import { Avatar, Badge, Button, Drawer, Popover, Tooltip } from '../../design-system';
import { useMessengerV2ControllerContext } from '../../features/messenger-v2/MessengerV2ControllerContext.jsx';
import { MessengerV2ActiveCallIndicator } from '../../features/messenger-v2/MessengerV2ActiveCallIndicator.jsx';
import {
  selectMessengerV2UnreadCount,
  selectMessengerV2UnreadDestination,
} from '../../features/messenger-v2/runtime/messengerUnreadState.js';
import { Metallic as MetallicIcon } from '../../design-system/icons';
import useIsMobile from '../../hooks/useIsMobile';
import useElementHeightCssVariable from '../../hooks/useElementHeightCssVariable.js';
import logoDark from '../../img/ikon_primr.png';
import logoLight from '../../img/logo_half.svg';
import {
  fetch_request,
  protectedUrl,
  resolvePostLogoutRedirect,
  revokeSession,
} from '../../service/request';
import { getFindGravePath, getFooterPagePath, isRoomsPath } from '../../service/constants';
import { resetReduxStoreAction, setConfirmSignOut } from '../../redux/actions';
import { unifiedSearchFetcher } from '../../pages/Search/fetcher';
import Notifications from '../Notifications/Notifications';
import HeaderSearch from '../Search/HeaderSearch';
import s from './HeaderBar.module.css';
import HeaderCenterIconNav from './HeaderCenterIconNav';

// The account menu is portaled to document.body and must stay above page-owned
// overlays (Messenger V2 uses layers up to 5500 for media/group surfaces).
const APP_HEADER_ACCOUNT_MENU_Z_INDEX = 6000;

export const HeaderBar = () => {
  const intl = useIntl();
  const messengerController = useMessengerV2ControllerContext();
  const headerRef = useRef(null);
  const dispatch = useDispatch();
  const currentUser = useSelector((s) => s?.user?.user || s?.user);
  const isAuth = useSelector((s) => s?.auth?.isAuthorized);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [systemBanner, setSystemBanner] = useState(null);
  const [dismissedBannerKey, setDismissedBannerKey] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem('ng.systemBanner.dismissed') || null;
    } catch {
      return null;
    }
  });
  const [footerMoreOpen, setFooterMoreOpen] = useState(false);
  const footerMenuRef = useRef(null);
  const [mobileSearchActive, setMobileSearchActive] = useState(false);
  useElementHeightCssVariable(headerRef, '--ng-header-h');
  const readTheme = () => {
    if (typeof document === 'undefined') return null;
    const attr = document.documentElement.getAttribute('data-theme');
    const stored = localStorage.getItem('theme');
    return attr || stored || null;
  };
  const [themeState, setThemeState] = useState(() => readTheme());
  const isMobile = useIsMobile();
  const name = currentUser?.full_name || currentUser?.name || 'User';
  const avatarSrc = currentUser?.avatar ? protectedUrl(currentUser.avatar) : null;
  const profileLink = currentUser?.slug ? `/members/${currentUser.slug}` : '/members/me';
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateThemeFromStorage = () => {
      setThemeState(readTheme());
    };
    updateThemeFromStorage();

    const handleStorage = (event) => {
      if (event.key === 'theme') {
        updateThemeFromStorage();
      }
    };

    window.addEventListener('storage', handleStorage);

    const observer = new MutationObserver(() => {
      updateThemeFromStorage();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });

    return () => {
      window.removeEventListener('storage', handleStorage);
      observer.disconnect();
    };
  }, []);

  const systemBannerKey = useMemo(() => {
    if (!systemBanner?.message) return null;
    if (systemBanner?.id) return `id:${systemBanner.id}`;
    return `${systemBanner.level || 'info'}::${systemBanner.message}`;
  }, [systemBanner?.id, systemBanner?.level, systemBanner?.message]);

  const canDismissSystemBanner = systemBanner?.level !== 'critical';
  const showSystemBanner = Boolean(
    systemBanner?.message && (!canDismissSystemBanner || dismissedBannerKey !== systemBannerKey),
  );

  const onDismissSystemBanner = () => {
    if (!canDismissSystemBanner || !systemBannerKey) return;
    setDismissedBannerKey(systemBannerKey);
    try {
      localStorage.setItem('ng.systemBanner.dismissed', systemBannerKey);
    } catch {
      // ignore
    }
  };

  const theme = themeState;
  const isDark = theme === 'dark' || theme === 'night';
  const themeToggleLabel = intl.formatMessage({
    id: isDark ? 'toggle_theme_to_light' : 'toggle_theme_to_dark',
    defaultMessage: isDark ? 'Switch to light mode' : 'Switch to dark mode',
  });
  const messagesLabel = intl.formatMessage({
    id: 'notifications.messaging.title',
    defaultMessage: 'Messages',
  });
  const unreadMessageCount = selectMessengerV2UnreadCount(messengerController?.state);
  const unreadMessagesDestination = selectMessengerV2UnreadDestination(messengerController?.state);
  const messagesAccessibleLabel = unreadMessageCount
    ? intl.formatMessage(
        {
          id: 'messenger.header.unseen_messages',
          defaultMessage: 'Messages, {count} new',
        },
        { count: unreadMessageCount },
      )
    : messagesLabel;
  const handleMessagesClick = useCallback(() => {
    if (isRoomsPath(location.pathname)) {
      window.location.assign(unreadMessagesDestination);
      return;
    }
    navigate(unreadMessagesDestination);
  }, [location.pathname, navigate, unreadMessagesDestination]);
  const renderIcon = useCallback(
    (Icon, props = {}) => {
      const { strokeWidth: propStrokeWidth, style: styleProp, ...rest } = props || {};
      const style = styleProp ? { ...styleProp } : {};
      // if (isDark) {
      //   const dropShadow = 'drop-shadow(0 0 3px rgba(255, 255, 255, 0.45))';
      //   style.filter = style.filter ? `${style.filter} ${dropShadow}` : dropShadow;
      // }
      const finalProps = {
        'aria-hidden': true,
        ...rest,
      };
      if (Object.keys(style).length > 0) {
        finalProps.style = style;
      }
      const strokeWidth = propStrokeWidth ?? (isDark ? 2.6 : 2);
      return isDark ? (
        <MetallicIcon Icon={Icon} variant="silver" strokeWidth={strokeWidth} {...finalProps} />
      ) : (
        <Icon {...finalProps} strokeWidth={strokeWidth} />
      );
    },
    [isDark],
  );

  const navIconMap = {
    home: Home,
    groups: DoorOpen,
    'family-tree': TreePine,
    graves: UploadCloud,
    store: HandHeart,
  };

  const handleFamilyTreeNavigate = useCallback(() => {
    const identifier = currentUser?.id;
    const viewerPersonId = currentUser?.id ? `u_${currentUser.id}` : null;
    if (!identifier) {
      navigate('/family-tree');
      return;
    }
    const rootId = `u_${identifier}`;
    try {
      localStorage.setItem('familyTree.lastId', rootId);
    } catch {
      /* ignore storage errors */
    }

    const params = new URLSearchParams();
    params.set('radius', '3');
    params.set('tab', 'tree');
    params.set('label_strategy', 'kinship');
    if (viewerPersonId) params.set('viewer_person_id', viewerPersonId);
    navigate(`/family-tree/${encodeURIComponent(rootId)}?${params.toString()}`);
  }, [currentUser?.id, navigate]);

  const gestUserMenu = [
    {
      label: <FormattedMessage id="sign_in" defaultMessage="Sign in" />,
      key: 'sign-in',
      icon: renderIcon(LogIn, { size: 16 }),
      link: '/sign-in',
    },
    {
      label: <FormattedMessage id="register" defaultMessage="Register" />,
      key: 'register',
      icon: renderIcon(UserPlus, { size: 16 }),
      link: '/register',
    },
    {
      label: <FormattedMessage id="nav.graves" defaultMessage="Graves" />,
      key: 'guest:graves',
      icon: renderIcon(UploadCloud, { size: 16 }),
      link: '/graves',
    },
    {
      label: <FormattedMessage id="nav.my_family" defaultMessage="My family" />,
      key: 'guest:family-tree',
      icon: renderIcon(TreePine, { size: 16 }),
      link: '/family-tree',
    },
    {
      label: <FormattedMessage id="my_light" defaultMessage="My Light" />,
      key: 'guest:my-light',
      icon: renderIcon(Lamp, { size: 16 }),
      link: getFooterPagePath('myLight'),
    },
    {
      label: <FormattedMessage id="footer_find_grave" defaultMessage="Find grave" />,
      key: 'guest:find-grave',
      icon: renderIcon(Search, { size: 16 }),
      link: getFindGravePath(),
    },
    {
      label: <FormattedMessage id="header.footer.short.how" defaultMessage="Hvordan" />,
      key: 'guest:how-it-works',
      icon: renderIcon(CircleHelp, { size: 16 }),
      link: getFooterPagePath('howItWorks'),
    },
    {
      label: <FormattedMessage id="footer_about" defaultMessage="About Nettgrav" />,
      key: 'guest:about',
      icon: renderIcon(Info, { size: 16 }),
      link: getFooterPagePath('about'),
    },
    {
      label: <FormattedMessage id="store" defaultMessage="Collection" />,
      key: 'guest:store',
      icon: renderIcon(HandHeart, { size: 16 }),
      link: '/store',
    },
    {
      label: <FormattedMessage id="tabs.more" defaultMessage="More" />,
      key: 'guest:more',
      icon: renderIcon(Menu, { size: 16 }),
      children: [
        {
          label: <FormattedMessage id="footer_create_spark" defaultMessage="Create spark" />,
          key: 'guest:create-spark',
          icon: renderIcon(Sparkles, { size: 16 }),
          link: getFooterPagePath('createSpark'),
        },
        {
          label: <FormattedMessage id="footer_partner" defaultMessage="Partner" />,
          key: 'guest:partner',
          icon: renderIcon(Users, { size: 16 }),
          link: getFooterPagePath('partner'),
        },
        {
          label: (
            <FormattedMessage id="footer_end_of_time_chat" defaultMessage="End of Time Chat" />
          ),
          key: 'guest:end-of-time-chat',
          icon: renderIcon(MessageCircle, { size: 16 }),
          link: getFooterPagePath('endOfTimeChat'),
        },
        {
          label: (
            <FormattedMessage id="footer_end_of_time_center" defaultMessage="End of Time Center" />
          ),
          key: 'guest:end-of-time-center',
          icon: renderIcon(Building2, { size: 16 }),
          link: getFooterPagePath('endOfTimeCenter'),
        },
      ],
    },
  ];

  const features = { store: true, familyTree: true }; // TODO: read real feature flags
  const primary = getPrimaryNav(intl, !!isAuth, features);

  const items = [
    ...(isAuth
      ? primary.map((p) => {
          const Icon = navIconMap[p.key];
          return {
            label: p.label,
            key: p.key,
            link: p.href,
            icon: Icon ? renderIcon(Icon, { size: 16 }) : undefined,
          };
        })
      : []),
    ...(isAuth
      ? [
          {
            label: <FormattedMessage id="nav.store" defaultMessage="Collection" />,
            key: 'store',
            link: '/store',
            icon: renderIcon(HandHeart, { size: 16 }),
          },
        ]
      : []),
    ...(isAuth
      ? [
          {
            label: <FormattedMessage id="my_light" defaultMessage="My Light" />,
            key: 'my-light',
            icon: renderIcon(Lamp, { size: 16 }),
            link: currentUser?.slug ? `/members/${currentUser.slug}` : '/members/me',
          },
        ]
      : []),
    ...(isAuth
      ? [
          {
            label: <FormattedMessage id="settings" defaultMessage="Settings" />,
            key: 'settings',
            icon: renderIcon(SettingsIcon, { size: 16 }),
            link: '/settings',
          },
        ]
      : []),
    ...(currentUser && currentUser?.admin && isAuth
      ? [
          {
            label: <FormattedMessage id="admin" defaultMessage="Admin" />,
            key: 'admin',
            icon: renderIcon(SettingsIcon, { size: 16 }),
            children: [
              {
                label: <FormattedMessage id="design_system" defaultMessage="Design system" />,
                key: 'admin:design-system',
                icon: renderIcon(Grid2X2, { size: 16 }),
                link: '/admin/design-system',
              },
              {
                label: <FormattedMessage id="products_add_product" defaultMessage="Add Product" />,
                key: 'admin:add-product',
                icon: renderIcon(PackagePlus, { size: 16 }),
                link: '/product/new',
              },
              {
                label: (
                  <FormattedMessage
                    id="admin.group_moderation.nav"
                    defaultMessage="Room moderation"
                  />
                ),
                key: 'admin:group-moderation',
                icon: renderIcon(SettingsIcon, { size: 16 }),
                link: '/admin/group-moderation',
              },
            ],
          },
        ]
      : []),
    // ...(isAuth
    //   ? [
    //       {
    //         label: <FormattedMessage id="help_and_support" defaultMessage="Help & support" />,
    //         key: 'help',
    //         icon: <LifeBuoy size={16} aria-hidden />,
    //         children: [
    //           {
    //             label: <FormattedMessage id="report_problem" defaultMessage="Report a problem" />,
    //             key: 'help:report',
    //             icon: <Bug size={16} aria-hidden />,
    //             link: '/help/report',
    //           },
    //           {
    //             label: <FormattedMessage id="contact_us" defaultMessage="Contact us" />,
    //             key: 'help:contact',
    //             icon: <Mail size={16} aria-hidden />,
    //             link: '/help/contact',
    //           },
    //         ],
    //       },
    //     ]
    //   : []),
    // Guest menu (Sign in / Register) when not authenticated
    ...(!isAuth ? gestUserMenu : []),
    ...(isAuth
      ? [
          {
            label: <FormattedMessage id="logout" defaultMessage="Sign out" />,
            key: 'sign-out',
            icon: renderIcon(LogOut, { size: 16 }),
          },
        ]
      : []),
  ];

  const footerPrimaryItems = isAuth
    ? [
        {
          label: (
            <FormattedMessage id="header.footer.short.find_grave" defaultMessage="Finn grav" />
          ),
          key: 'footer:find-grave',
          link: getFindGravePath(),
        },
        {
          label: <FormattedMessage id="header.footer.short.how" defaultMessage="Hvordan" />,
          key: 'footer:how-it-works',
          link: getFooterPagePath('howItWorks'),
        },
        {
          label: <FormattedMessage id="header.footer.short.about" defaultMessage="Om" />,
          key: 'footer:about',
          link: getFooterPagePath('about'),
        },
        {
          label: <FormattedMessage id="contact_us" defaultMessage="Contact us" />,
          key: 'footer:contact',
          link: 'mailto:kontakt@nettgrav.no',
        },
      ]
    : [];

  const footerMoreItems = isAuth
    ? [
        {
          label: <FormattedMessage id="footer_partner" defaultMessage="Partner" />,
          key: 'footer:partner',
          link: getFooterPagePath('partner'),
        },
        {
          label: (
            <FormattedMessage id="footer_end_of_time_chat" defaultMessage="End of Time Chat" />
          ),
          key: 'footer:end-of-time-chat',
          link: getFooterPagePath('endOfTimeChat'),
        },
        {
          label: (
            <FormattedMessage id="footer_end_of_time_center" defaultMessage="End of Time Center" />
          ),
          key: 'footer:end-of-time-center',
          link: getFooterPagePath('endOfTimeCenter'),
        },
      ]
    : [];

  const footerCompactMoreItems = isAuth
    ? [
        {
          label: <FormattedMessage id="footer_graves" defaultMessage="Graves" />,
          key: 'footer:graves',
          link: '/search/graves',
        },
        {
          label: (
            <FormattedMessage id="footer_create_nettgrav" defaultMessage="Create a Nettgrav" />
          ),
          key: 'footer:create-nettgrav',
          link: getFooterPagePath('createNettgrav'),
        },
        {
          label: <FormattedMessage id="footer_create_spark" defaultMessage="Create spark" />,
          key: 'footer:create-spark',
          link: getFooterPagePath('createSpark'),
        },
        ...footerMoreItems,
      ]
    : [];

  const navigateTo = useCallback(
    (target, options = {}) => {
      if (!target) return;
      navigate(target, options);
    },
    [navigate],
  );

  const onSelect = (item) => {
    // Close the popover before navigating to ensure overlay doesn't block UI
    setMenuOpen(false);
    setFooterMoreOpen(false);

    if (item?.key === 'family-tree') {
      handleFamilyTreeNavigate();
      return;
    }
    if (item?.link) {
      if (/^(mailto:|https?:\/\/)/i.test(item.link)) {
        window.location.assign(item.link);
        return;
      }
      navigateTo(item.link);
      return;
    }
    if (item?.key === 'sign-out') {
      (async () => {
        try {
          await revokeSession();
        } catch {}
        dispatch(resetReduxStoreAction());
        dispatch(setConfirmSignOut());
        try {
          clearLocalStoragePreserveMls();
          clearSessionStoragePreserveMls();
        } catch {}
        window.dispatchEvent(new Event('storage'));
        const target = resolvePostLogoutRedirect(location.pathname, location.search, location.hash);
        if (typeof window !== 'undefined') {
          window.location.assign(target);
          return;
        }
        navigateTo(target, { replace: true });
      })();
    }
  };

  const isActiveLink = (link) => {
    if (!link) return false;
    const cur = location.pathname;
    if (cur === link) return true;
    // Treat parent paths as active for nested routes
    try {
      const u = new URL(link, window.location.origin);
      return cur.startsWith(u.pathname);
    } catch {
      return cur.startsWith(link);
    }
  };

  const renderMenuItems = (menuItems) => (
    <div className={s.col}>
      {menuItems.map((it) => (
        <div key={it.key}>
          {it.children ? (
            <>
              <div className={s.sectionLabel}>
                {it.icon && <span className={s.sectionIcon}>{it.icon}</span>}
                <span>{it.label}</span>
              </div>
              <div className={s.children}>
                {it.children.map((c) => (
                  <Button
                    key={c.key}
                    size="small"
                    block
                    variant={isActiveLink(c.link) ? 'filled' : 'text'}
                    color={isActiveLink(c.link) ? 'primary' : undefined}
                    leftIcon={c.icon}
                    className={s.start}
                    style={{ width: '100%', justifyContent: 'flex-start' }}
                    onClick={() => onSelect(c)}
                  >
                    {c.label}
                  </Button>
                ))}
              </div>
            </>
          ) : (
            <Button
              size="small"
              block
              variant={isActiveLink(it.link) ? 'filled' : 'text'}
              color={isActiveLink(it.link) ? 'primary' : undefined}
              leftIcon={it.icon}
              className={s.start}
              style={{ width: '100%', justifyContent: 'flex-start' }}
              onClick={() => onSelect(it)}
            >
              {it.label}
            </Button>
          )}
        </div>
      ))}
    </div>
  );

  const menuContent = (
    <div className={s.menu}>
      {isAuth && (
        <div className={s.menuHeader}>
          <div
            tabIndex={0}
            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            onClick={() => {
              setMenuOpen(false);
              navigateTo(profileLink);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setMenuOpen(false);
                navigateTo(profileLink);
              }
            }}
          >
            <Avatar src={avatarSrc} initials={(name || 'U').slice(0, 2).toUpperCase()} size={32} />
            <div className={s.text}>
              <div className={s.name}>{name}</div>
              <div className={s.account}>
                <FormattedMessage id="my_light" defaultMessage="My Light" />
              </div>
            </div>
          </div>
          {isMobile && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <NightModeToggle
                btnVariant="filled"
                btnColor="var(--surface-3)"
                style={{ color: 'var(--text)' }}
              />
            </div>
          )}
        </div>
      )}
      {!isAuth && (
        <div className={s.menuHeader}>
          <div className={s.text}>
            <div className={s.name}>
              <FormattedMessage id="guest_menu" defaultMessage="Menu" />
            </div>
            <div className={s.account}>
              <FormattedMessage
                id="guest_menu.subtitle"
                defaultMessage="Explore more of Nettgrav or sign in to continue."
              />
            </div>
          </div>
          {isMobile && (
            <div
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <NightModeToggle
                btnVariant="filled"
                btnColor="var(--surface-3)"
                style={{ color: 'var(--text)' }}
              />
            </div>
          )}
        </div>
      )}
      <div className={s.menuBody}>{renderMenuItems(items)}</div>
      {isAuth && footerPrimaryItems.length > 0 && (
        <div className={s.menuFooter} ref={footerMenuRef}>
          <div className={s.menuFooterLinks}>
            {footerPrimaryItems.map((it) => (
              <Button
                key={it.key}
                size="small"
                variant="text"
                className={s.footerLink}
                onClick={() => onSelect(it)}
              >
                {it.label}
              </Button>
            ))}
            <Button
              size="small"
              variant="text"
              className={s.footerLink}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFooterMoreOpen((prev) => !prev);
              }}
            >
              <FormattedMessage id="tabs.more" defaultMessage="More" />
            </Button>
          </div>
          {footerMoreOpen && (
            <div className={s.footerMoreMenu}>
              {footerCompactMoreItems.map((it) => (
                <Button
                  key={it.key}
                  size="small"
                  variant="text"
                  className={s.footerMoreItem}
                  onClick={() => onSelect(it)}
                >
                  {it.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const menuButtonCore = (
    <Button
      size="small"
      variant="filled"
      color="var(--surface-3)"
      style={{ color: 'var(--text)' }}
      rightIcon={<Menu size={16} />}
      onClick={isMobile ? () => setMenuOpen(true) : undefined}
    />
  );

  const menuButton = isMobile ? (
    menuButtonCore
  ) : (
    <Tooltip
      content={intl.formatMessage({
        id: isAuth ? 'account_menu' : 'guest_menu',
        defaultMessage: isAuth ? 'Account menu' : 'Menu',
      })}
      placement="bottom"
      tipClassName={s.menuTooltip}
    >
      {menuButtonCore}
    </Tooltip>
  );

  const handleSearchOpenChange = (next) => {
    if (isMobile) {
      setMobileSearchActive(next);
    }
  };

  useEffect(() => {
    if (!isMobile && mobileSearchActive) {
      setMobileSearchActive(false);
    }
  }, [isMobile, mobileSearchActive]);

  useEffect(() => {
    if (!menuOpen) {
      setFooterMoreOpen(false);
    }
  }, [menuOpen]);

  useEffect(() => {
    let mounted = true;

    const loadSystemBanner = async () => {
      try {
        const res = await fetch_request('/api/system_banner', {
          cache: 'no-store',
          cacheKey: '/api/system_banner:live',
        });
        if (!res?.ok || !mounted) return;
        const data = await res.json();
        if (!mounted) return;
        if (data?.active && data?.message) {
          setSystemBanner({
            id: data.id || null,
            message: data.message,
            level: data.level || 'info',
          });
        } else {
          setSystemBanner(null);
        }
      } catch {
        if (mounted) setSystemBanner(null);
      }
    };

    loadSystemBanner();
    const intervalId = window.setInterval(loadSystemBanner, 120000);

    return () => {
      mounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const centerNav =
    !isMobile && !mobileSearchActive ? (
      <div className={s.centerRow}>
        <HeaderCenterIconNav />
      </div>
    ) : null;
  const messagesButton = (
    <Badge
      count={unreadMessageCount}
      overflowCount={9}
      showZero={false}
      offset={[-4, 0]}
      size="small"
      color="var(--brand)"
    >
      <Button
        size="small"
        shape="circle"
        variant="filled"
        color="var(--surface-3)"
        style={{ color: 'var(--text)' }}
        aria-label={messagesAccessibleLabel}
        onClick={handleMessagesClick}
        icon={<MessageCircle size={16} aria-hidden />}
      />
    </Badge>
  );
  const collectionLabel = intl.formatMessage({
    id: 'nav.store',
    defaultMessage: 'Collection',
  });
  const collectionButtonCore = (
    <Button
      size="small"
      shape="circle"
      variant="filled"
      color="var(--surface-3)"
      style={{ color: 'var(--text)' }}
      aria-label={collectionLabel}
      onClick={() => navigateTo('/store')}
      icon={renderIcon(HandHeart, { size: 16 })}
    />
  );
  const collectionButton = isMobile ? (
    collectionButtonCore
  ) : (
    <Tooltip content={collectionLabel} placement="bottom">
      {collectionButtonCore}
    </Tooltip>
  );
  const logo = isDark ? logoLight : logoDark;

  return (
    <header ref={headerRef} className={s.bar}>
      <div className={s.inner}>
        <div className={s.topRow}>
          <div className={s.leftRow}>
            {(!isMobile || !mobileSearchActive) && (
              <Button
                type="default"
                size="small"
                variant="filled"
                style={{ flexShrink: 0 }}
                onClick={() => navigateTo(isAuth ? '/sparks' : '/')}
                icon={<img src={logo} alt="Logo" style={{ height: '16px' }} />}
              />
            )}
            <div
              className={`${s.searchWrap} ${
                isMobile && mobileSearchActive ? s.searchWrapExpanded : ''
              }`}
            >
              <HeaderSearch
                fetcher={unifiedSearchFetcher}
                // Hide the compact opener on the full results page (keeps UX clean)
                hideOnPaths={['/search']}
                onOpenChange={handleSearchOpenChange}
              />
            </div>
          </div>
          {/* NEW: icons-only center nav (absolute centered; desktop area) */}
          {centerNav}
          {(!isMobile || !mobileSearchActive) && (
            <div className={s.rightRow}>
              {isAuth && !isMobile && (
                <Tooltip
                  content={themeToggleLabel}
                  placement="bottom"
                  size="small"
                  variant="filled"
                  color="var(--surface-3)"
                  shape="circle"
                  style={{ color: 'var(--text)' }}
                >
                  <NightModeToggle
                    btnVariant="filled"
                    btnColor="var(--surface-3)"
                    style={{ color: 'var(--text)' }}
                  />
                </Tooltip>
              )}

              {isAuth ? collectionButton : null}

              {isAuth &&
                (!isMobile ? (
                  <Tooltip
                    content={messagesAccessibleLabel}
                    placement="bottom"
                    size="small"
                    variant="filled"
                    color="var(--surface-3)"
                    shape="circle"
                    style={{ color: 'var(--text)' }}
                  >
                    {messagesButton}
                  </Tooltip>
                ) : (
                  messagesButton
                ))}

              {!isAuth &&
                (isMobile ? (
                  <NightModeToggle
                    btnVariant="filled"
                    btnColor="var(--surface-3)"
                    style={{ color: 'var(--text)' }}
                  />
                ) : (
                  <Tooltip
                    content={themeToggleLabel}
                    placement="bottom"
                    size="small"
                    variant="filled"
                    color="var(--surface-3)"
                    shape="circle"
                    style={{ color: 'var(--text)' }}
                  >
                    <NightModeToggle
                      btnVariant="filled"
                      btnColor="var(--surface-3)"
                      style={{ color: 'var(--text)' }}
                    />
                  </Tooltip>
                ))}

              {!isAuth ? collectionButton : null}

              {!isMobile && !isAuth && (
                <>
                  <Button
                    size="small"
                    variant="filled"
                    color="var(--surface-3)"
                    className="ds-btn--surface-sky"
                    onClick={() => navigateTo('/sign-in')}
                  >
                    <FormattedMessage id="sign_in" defaultMessage="Sign in" />
                  </Button>
                  <Button
                    size="small"
                    variant="filled"
                    color="var(--sky-3)"
                    className={s.skyActionButton}
                    style={{ color: 'var(--sky-10)' }}
                    onClick={() => navigateTo('/register')}
                  >
                    <FormattedMessage id="register" defaultMessage="Register" />
                  </Button>
                </>
              )}

              {isAuth && <Notifications />}

              {!isMobile && isAuth && (
                <Tooltip content={name} placement="bottom" className={s.inlineCenter}>
                  <Avatar
                    src={avatarSrc}
                    initials={(name || 'U').slice(0, 2).toUpperCase()}
                    size={32}
                    onClick={() => navigateTo(profileLink)}
                  />
                </Tooltip>
              )}

              {(isAuth || !isAuth) &&
                (isMobile ? (
                  <>
                    {menuButton}
                    <Drawer
                      open={menuOpen}
                      onClose={() => setMenuOpen(false)}
                      side="right"
                      className={s.drawer}
                    >
                      <div className={s.drawerInner}>
                        <div className={s.drawerHeader}>
                          <Button
                            variant="text"
                            shape="circle"
                            size="small"
                            aria-label={intl.formatMessage({
                              id: 'auth_back',
                              defaultMessage: 'Back',
                            })}
                            onClick={() => setMenuOpen(false)}
                          >
                            {renderIcon(ChevronLeft, { size: 18 })}
                          </Button>
                          <span className={s.drawerHeaderTitle}>
                            <FormattedMessage
                              id={isAuth ? 'account_menu' : 'guest_menu'}
                              defaultMessage={isAuth ? 'Account menu' : 'Menu'}
                            />
                          </span>
                        </div>
                        {menuContent}
                      </div>
                    </Drawer>
                  </>
                ) : (
                  <Popover
                    placement="bottomRight"
                    trigger={['click']}
                    arrow={false}
                    closeOnBlur={false}
                    open={menuOpen}
                    onOpenChange={setMenuOpen}
                    content={menuContent}
                    contentClassName={s.menuPopoverContent}
                    destroyOnHidden
                    zIndex={APP_HEADER_ACCOUNT_MENU_Z_INDEX}
                  >
                    {menuButton}
                  </Popover>
                ))}
            </div>
          )}
        </div>
        <MessengerV2ActiveCallIndicator />
        {showSystemBanner ? (
          <div
            className={`${s.systemBanner} ${s[`systemBanner${systemBanner.level}`] || ''}`}
            role="status"
          >
            <span className={s.systemBannerText}>{systemBanner.message}</span>
            {canDismissSystemBanner ? (
              <Button
                variant="text"
                size="small"
                shape="circle"
                className={s.systemBannerClose}
                onClick={onDismissSystemBanner}
                aria-label={intl.formatMessage({
                  id: 'close',
                  defaultMessage: 'Close',
                })}
                icon={renderIcon(X, { size: 14 })}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default HeaderBar;
